import React, { useEffect, useState } from 'react';
import { Card, Typography, Space, Tag, Spin, Alert, Row, Col, Collapse } from 'antd';
import { UserOutlined, MedicineBoxOutlined, DownOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { PrescriptionDetails as PrescriptionDetailsType } from '../store/slices/prescriptionSlice';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const PrescriptionDetails: React.FC = () => {
  const { prescriptionDetails, loading, error } = useSelector(
    (state: RootState) => state.prescription
  );
  const [activeKey, setActiveKey] = useState<string[]>([]);

  // Extract the prescription data from the API response
  const prescriptionData = (prescriptionDetails as PrescriptionDetailsType)?.data;

  // Auto-expand when prescription details are loaded
  useEffect(() => {
    if (prescriptionDetails && prescriptionData && prescriptionData.prescription) {
      const keys = [];
      if (prescriptionData.doctorDetails) keys.push('doctor');
      if (prescriptionData.prescription.Medicines && prescriptionData.prescription.Medicines.length > 0) {
        keys.push('medicines');
      }
      setActiveKey(keys);
    } else {
      setActiveKey([]);
    }
  }, [prescriptionDetails, prescriptionData]);

  if (loading) {
    return (
      <div style={{ marginTop: '16px' }}>
        <Collapse ghost>
          <Panel 
            header={
              <Title level={5} style={{ margin: 0 }}>
                <MedicineBoxOutlined /> Prescription Details
              </Title>
            } 
            key="1"
          >
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <Spin size="small" /> <Text type="secondary">Loading prescription details...</Text>
            </div>
          </Panel>
        </Collapse>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ marginTop: '16px' }}>
        <Collapse ghost>
          <Panel 
            header={
              <Title level={5} style={{ margin: 0 }}>
                <MedicineBoxOutlined /> Prescription Details
              </Title>
            } 
            key="1"
          >
            <Alert
              message="Error Loading Prescription"
              description={error}
              type="error"
              showIcon
            />
          </Panel>
        </Collapse>
      </div>
    );
  }

  if (!prescriptionDetails || !prescriptionData) {
    return null;
  }

  if (!prescriptionData.prescription) {
    return (
      <div style={{ marginTop: '16px' }}>
        <Collapse ghost>
          <Panel 
            header={
              <Title level={5} style={{ margin: 0 }}>
                <MedicineBoxOutlined /> Prescription Details
              </Title>
            } 
            key="1"
          >
            <Alert
              message="No Prescriptions Found"
              description="This patient has no prescription records available."
              type="info"
              showIcon
            />
          </Panel>
        </Collapse>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '16px' }}>
      <Collapse 
        activeKey={activeKey}
        onChange={(keys) => setActiveKey(keys as string[])}
        ghost
        expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
      >
        {/* Doctor Information Panel */}
        {prescriptionData?.doctorDetails && (
          <Panel 
            header={
              <Title level={5} style={{ margin: 0 }}>
                <UserOutlined /> Doctor: {prescriptionData.doctorDetails.firstName} {prescriptionData.doctorDetails.lastName}
              </Title>
            } 
            key="doctor"
          >
            <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
              <Text strong style={{ fontSize: '16px' }}>
                {prescriptionData.doctorDetails.firstName} {prescriptionData.doctorDetails.lastName}
              </Text>
            </Card>
          </Panel>
        )}

        {/* Medicines Panel */}
        {prescriptionData?.prescription?.Medicines && prescriptionData.prescription.Medicines.length > 0 && (
          <Panel 
            header={
              <Title level={5} style={{ margin: 0 }}>
                <MedicineBoxOutlined /> Medicines ({prescriptionData.prescription.Medicines.filter((medicine: any) => medicine.name && medicine.name.trim() !== '').length})
              </Title>
            } 
            key="medicines"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {prescriptionData.prescription.Medicines
                .filter((medicine: any) => medicine.name && medicine.name.trim() !== '') // Filter out empty medicines
                .map((medicine: any, index: number) => (
                <Card key={index} size="small" style={{ backgroundColor: '#f9f9f9' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text strong style={{ fontSize: '16px' }}>{medicine.name}</Text>
                    <Row gutter={[16, 8]}>
                      <Col span={8}>
                        <Text type="secondary">Dosage: </Text>
                        <Text strong>{medicine.dosage}</Text>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary">Frequency: </Text>
                        <Text strong>{medicine.frequency}</Text>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary">Type: </Text>
                        <Tag color="blue">{medicine.type}</Tag>
                      </Col>
                    </Row>
                    {medicine.instruction && (
                      <div style={{ marginTop: '8px' }}>
                        <Text type="secondary">Instructions: </Text>
                        <Text>{medicine.instruction}</Text>
                      </div>
                    )}
                  </Space>
                </Card>
              ))}
            </Space>
          </Panel>
        )}
      </Collapse>
    </div>
  );
};

export default PrescriptionDetails;
